/**
 * Relay, Switch, Roller, Emeter, Thermostat, Lock, Breaker action preparations
 */

import { convertUiPropertyToSceneCommand } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	RelayActionHandler,
	RelayHandlerResult,
} from 'src/components/Scenes/helpers/device_actions/types';
import type { DeviceAction } from 'src/types/DeviceActionUI';

/**
 * Resolves the effective channel for a relay action, accounting for addon indices and mode-specific overrides.
 *
 * @param context - Shared device action context.
 * @param action - Device action whose channel should be resolved.
 * @returns Channel index appropriate for the given action.
 */
const resolveRelayChannel = (context: DeviceActionContext, action: DeviceAction): number => {
	let channel = context.channel;

	if (typeof action?.addOnInd === 'number') {
		channel = action.addOnInd;
	}

	if (context.mode === 'emeter' && action.name === 'turn') {
		channel = 0;
	}

	return channel;
};

/**
 * Generates a relay handler result for the provided action.
 *
 * @param context - Shared device action context.
 * @param action - Device action to convert into a scene payload.
 * @param channelOverride - Optional channel override for special handlers.
 * @returns Relay handler result containing the command and value to be sent.
 */
const createRelayHandlerResult = (
	context: DeviceActionContext,
	action: DeviceAction,
	channelOverride?: number,
): RelayHandlerResult => {
	const channel = channelOverride ?? resolveRelayChannel(context, action);

	return {
		command: convertUiPropertyToSceneCommand(action.name, channel),
		value: action.value,
	};
};

/**
 * Default handler for relay actions that require no custom behaviour.
 */
const defaultRelayActionHandler: RelayActionHandler = (context, action) =>
	createRelayHandlerResult(context, action);

/**
 * Specialized handler for `turn`/`state` actions to support emeter channel overrides.
 */
const handleRelayTurnAction: RelayActionHandler = (context, action) => {
	const channelOverride = context.mode === 'emeter' ? 0 : undefined;
	return createRelayHandlerResult(context, action, channelOverride);
};

/**
 * Mapping of relay action names to their specialized handlers.
 */
const RELAY_ACTION_HANDLERS: Partial<Record<string, RelayActionHandler>> = {
	turn: handleRelayTurnAction,
	state: handleRelayTurnAction,
};

/**
 * List of action names with specialized handling logic for relay devices.
 */
export const RELAY_SUPPORTED_ACTIONS = Object.keys(RELAY_ACTION_HANDLERS);

/**
 * Prepares action payloads for relay-like devices (relay, output_reader, roller, emeter, thermostat, lock, breaker).
 *
 * These devices typically execute only one action at a time.
 *
 * @param context - Context containing shared helpers, channel metadata, and queued device actions.
 * Specialized action names are listed in `RELAY_SUPPORTED_ACTIONS`.
 * @returns Prepared action payload ready for dispatch to the device.
 */
export const prepareRelayAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;
	const primaryAction = actions[0];

	if (!primaryAction) {
		return {
			_gui_purpose: 'action',
		};
	}

	const handler = RELAY_ACTION_HANDLERS[primaryAction.name] ?? defaultRelayActionHandler;
	const { command, value } = handler(context, primaryAction);

	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':' + command,
		set: value,
	};

	return action;
};

/**
 * Determines whether the provided mode represents a relay-like device.
 *
 * @param mode - Device mode identifier.
 * @returns `true` if the mode corresponds to a relay-like device, otherwise `false`.
 */
export const isRelayLikeMode = (mode: string): boolean => {
	return [
		'relay',
		'output_reader',
		'roller',
		'emeter',
		'thermostat',
		'monophase',
		'triphase',
		'lock',
		'breaker',
	].includes(mode);
};
